import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { watchContext } from '../../base/context';
import { ElementLogger } from '../../base/logger';
import { DEV_MODE } from '../../global/env';
import { ManagedControls, mediaContext } from '../../media';
import { setAttribute } from '../../utils/dom';
import { controlsElementStyles } from './styles';
export const CONTROLS_ELEMENT_TAG_NAME = 'vds-controls';
/**
 * Container for holding individual media controls.
 *
 * 💡 The following attributes are updated for your styling needs:
 *
 * - `hidden`: Applied when the controls should be hidden and not available to the user.
 * - `idle`: Applied when there is no user activity for a set period, `hidden` should have greater priority.
 * - `media-autoplay-error`: Applied when media autoplay fails. It can be used to show recovery UI
 * if controls are hidden.
 * - `media-can-play`: Applied when media can begin playback.
 * - `media-paused`: Applied when media is paused.
 * - `media-view-type`: Applied with the media view type such as `audio` or `video`.
 *
 * @tagname vds-controls
 * @slot Used to pass in controls.
 * @example
 * ```html
 * <vds-controls>
 *   <vds-play-button></vds-play-button>
 *   <vds-scrubber></vds-scrubber>
 *   <vds-fullscreen-button></vds-fullscreen-button>
 * </vds-controls>
 * ```
 * @example
 * ```css
 * vds-controls {
 *   opacity: 1;
 *   transition: opacity 0.3s ease-out;
 * }
 *
 * vds-controls[idle] {
 *   opacity: 0;
 * }
 * ```
 */
export class ControlsElement extends LitElement {
    constructor() {
        super(...arguments);
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
        this._managedControls = new ManagedControls(this);
    }
    static get styles() {
        return [controlsElementStyles];
    }
    render() {
        return html `<slot></slot>`;
    }
    _handleCustomControlsContextUpdate(showing) {
        setAttribute(this, 'hidden', !showing);
    }
    _handleIdleContextUpdate(idle) {
        setAttribute(this, 'idle', idle);
    }
    _handleMediaAutoplayErrorContextUpdate(error) {
        setAttribute(this, 'media-autoplay-error', !!error);
    }
    _handleCanPlayContextUpdate(canPlay) {
        setAttribute(this, 'media-can-play', canPlay);
    }
    _handlePausedContextUpdate(paused) {
        setAttribute(this, 'media-paused', paused);
    }
    _handleViewTypeContextUpdate(viewType) {
        setAttribute(this, 'media-view-type', viewType);
    }
}
__decorate([
    watchContext(mediaContext.customControls)
], ControlsElement.prototype, "_handleCustomControlsContextUpdate", null);
__decorate([
    watchContext(mediaContext.idle)
], ControlsElement.prototype, "_handleIdleContextUpdate", null);
__decorate([
    watchContext(mediaContext.autoplayError)
], ControlsElement.prototype, "_handleMediaAutoplayErrorContextUpdate", null);
__decorate([
    watchContext(mediaContext.canPlay)
], ControlsElement.prototype, "_handleCanPlayContextUpdate", null);
__decorate([
    watchContext(mediaContext.paused)
], ControlsElement.prototype, "_handlePausedContextUpdate", null);
__decorate([
    watchContext(mediaContext.viewType)
], ControlsElement.prototype, "_handleViewTypeContextUpdate", null);
